import { Button, Container, Typography } from '@/ui-kit';
import styles from './liked-list.module.scss';
import { ErrorNotificationIcon, MagicStick, ModerationPendingIcon, RemoderationIcon, SuccessNotificationIcon } from '@/assets/icons';
import { routesApp } from '@/constans/routes';
import { useAuth } from '@/store/useAuth';
import { Like } from '@/types/profile';
import { LikedItem } from './componenets/liked-item';
import { VoteProfileDto } from '@/api/profiles';
import { useVoteProfileById } from '@/hooks/use-vote-profile-by-id';
import React from 'react';
import { useToast } from '@/store/useToast';
import clsx from 'clsx';
import WebApp from '@twa-dev/sdk';
import { useVibration } from '@/store/useVibration';
import { HalfDone } from '../half-done';
import { Trans, useTranslation } from 'react-i18next';
import { PremiumBanner } from '../premium-banner';

interface Props {
    paidLikes: Like[];
    freeLikes: Like[];
    refetchData?: () => void;
}

export const LikedList: React.FC<Props> = ({ paidLikes = [], freeLikes = [], refetchData }) => {
    const userData = useAuth((state) => state.getUser());
    const showToast = useToast((state) => state.show);
    const { mutateAsync: voteProfileById } = useVoteProfileById();
    const { t } = useTranslation();
    const handleVoteProfileById = async (profileId: number, voteProfileDto: VoteProfileDto) => {
        try {
            const { data } = await voteProfileById({
                profileId,
                voteProfileDto,
            });

            if (voteProfileDto.value === false) {
                refetchData?.();
            }

            if (voteProfileDto.value === true) {
                refetchData?.();
            }

            showToast({
                icon: <SuccessNotificationIcon />,
                msg: voteProfileDto.value ? t('likes.like_saved') : t('likes.dislike_saved')
            });
        } catch (e) {
            showToast({
                // type error
                icon: <ErrorNotificationIcon />,
                msg: t('error_try_again')
            });
        }
    };
    const isPaid = userData?.is_paid;

    const noLikeYet = (
        <div className={styles.noLikeWrapper}>
            <div className={styles.noLike}>
                <div className={styles.noLike__dekor} />
                <Typography
                    className={styles.noLike__title}
                    center
                    fontWeight="semibold"
                    fontFamily="SFProText">
                    {t('likes.empty_list')}
                </Typography>

                <Typography
                    className={styles.noLike__titleText}
                    center
                    fontWeight="semibold"
                    fontFamily="SFProText">
                    <Trans i18nKey="likes.liked_list_desc" />
                </Typography>

                <Button
                    className={styles.noLike__button}
                    isLink
                    to={routesApp.browse}
                    typeColor="orange"
                    textColor="white">
                    {t('find_a_pair')}
                </Button>
            </div>
            {!isPaid && <PremiumBanner />}
        </div>
    );

    const paidProfileList =
        userData
            ?.moderation_status
            === 'change_required' ?
            (
                userData
                    .violations_resolved ?
                    <div className={styles.remoderationCard}>
                        <ModerationPendingIcon
                            width={56}
                            height={56}
                            className={styles.remoderationCardIconPending} />
                        <Typography
                            className={styles.moderationTitle}
                            center
                            fontWeight="semibold"
                            fontFamily="SFProText">
                            {t('your_profile_is_being_reviewed')}
                        </Typography>

                        <Typography
                            center
                            fontFamily="SFProText"
                            className={styles.moderationSubtitle}>
                            {t('we_are_currently_checking_your_info')}
                        </Typography>
                    </div>
                :
                    <div className={styles.remoderationCard}>
                        <RemoderationIcon
                            width={56}
                            height={56}
                            className={styles.remoderationCardIcon} />
                        <Typography
                            className={styles.moderationTitle}
                            center
                            fontWeight="semibold"
                            fontFamily="SFProText">
                            <Trans i18nKey="please_edit_your_details" />
                        </Typography>

                        <Typography
                            center
                            fontFamily="SFProText"
                            className={styles.moderationSubtitle}>
                            {t('passing_verification_note')}
                        </Typography>

                        <Button
                            className={styles.moderationBtn}
                            isLink
                            to={routesApp.profileEdit}
                            typeColor="orange"
                            textColor="white">
                            {t('go_to_profile')}
                        </Button>
                    </div>
            )
        : paidLikes.length > 0 ?
            <div className={clsx(!isPaid && styles.paidLikesForUnpremiumUser)}>
                <Typography
                    className={styles.premiumLikesTitle}
                    fontWeight="semibold"
                    fontFamily="SFProText">
                    {t('likes.premium_likes')}
                </Typography>
                {
                    paidLikes.map(like => (
                        <LikedItem
                            key={like.id}
                            like={like}
                            isPaid={
                                (isPaid || false) ||
                                like.profile.is_paid
                            }
                            onVoteProfile={value => handleVoteProfileById(like.profile.id, { value })} />
                    ))
                }
            </div>
        :
            <></>;

    const freeProfileList =
        userData
            ?.moderation_status
            === 'change_required' ?
            (
                userData
                    .violations_resolved ?
                    <div className={styles.remoderationCard}>
                        <ModerationPendingIcon
                            width={56}
                            height={56}
                            className={styles.remoderationCardIconPending} />
                        <Typography
                            className={styles.moderationTitle}
                            center
                            fontWeight="semibold"
                            fontFamily="SFProText">
                            {t('your_profile_is_being_reviewed')}
                        </Typography>

                        <Typography
                            center
                            fontFamily="SFProText"
                            className={styles.moderationSubtitle}>
                            {t('we_are_currently_checking_your_info')}
                        </Typography>
                    </div>
                :
                    <div className={styles.remoderationCard}>
                        <RemoderationIcon
                            width={56}
                            height={56}
                            className={styles.remoderationCardIcon} />
                        <Typography
                            className={styles.moderationTitle}
                            center
                            fontWeight="semibold"
                            fontFamily="SFProText">
                            <Trans i18nKey="please_edit_your_details" />
                        </Typography>

                        <Typography
                            center
                            fontFamily="SFProText"
                            className={styles.moderationSubtitle}>
                            {t('passing_verification_note')}
                        </Typography>

                        <Button
                            className={styles.moderationBtn}
                            isLink
                            to={routesApp.profileEdit}
                            typeColor="orange"
                            textColor="white">
                            {t('go_to_profile')}
                        </Button>
                    </div>
            )
        :
            freeLikes.map(like => (
                <LikedItem
                    key={like.id}
                    like={like}
                    isPaid={
                        (isPaid || false) ||
                        like.profile.is_paid
                    }
                    onVoteProfile={value => handleVoteProfileById(like.profile.id, { value })} />
              ));

    const { isVibrationEnabled } =
        useVibration(state => state);
    
    const onActivatePremiumClick = () => {
        if (isVibrationEnabled) {
            WebApp
                .HapticFeedback
                .impactOccurred('medium');
            // pass for not android
            navigator.vibrate?.(10)
        }
    }

    return (
        <Container
            className={
                clsx(
                    styles.container,
                    (
                        [
                            ...paidLikes,
                            ...freeLikes
                        ]
                            .length === 0 ||
                        userData
                            ?.moderation_status
                            === 'change_required'
                    ) &&
                    styles.containerCenter,
                    (
                        userData
                            ?.moderation_status
                            === 'change_required'
                    ) &&
                    styles.moderationContainer
                )
            }>
            {
                userData
                    ?.moderation_status
                    === 'change_required' ?
                    (
                        userData
                            .violations_resolved ?
                            <div className={styles.remoderationCard}>
                                <ModerationPendingIcon
                                    width={56}
                                    height={56}
                                    className={styles.remoderationCardIconPending} />
                                <Typography
                                    className={styles.moderationTitle}
                                    center
                                    fontWeight="semibold"
                                    fontFamily="SFProText">
                                    {t('your_profile_is_being_reviewed')}
                                </Typography>

                                <Typography
                                    center
                                    fontFamily="SFProText"
                                    className={styles.moderationSubtitle}>
                                    {t('we_are_currently_checking_your_info')}
                                </Typography>
                            </div>
                        :
                            <div className={styles.remoderationCard}>
                                <RemoderationIcon
                                    width={56}
                                    height={56}
                                    className={styles.remoderationCardIcon} />
                                <Typography
                                    className={styles.moderationTitle}
                                    center
                                    fontWeight="semibold"
                                    fontFamily="SFProText">
                                    <Trans i18nKey="please_edit_your_details" />
                                </Typography>

                                <Typography
                                    center
                                    fontFamily="SFProText"
                                    className={styles.moderationSubtitle}>
                                    {t('passing_verification_note')}
                                </Typography>

                                <Button
                                    className={styles.moderationBtn}
                                    isLink
                                    to={routesApp.profileEdit}
                                    typeColor="orange"
                                    textColor="white">
                                    {t('go_to_profile')}
                                </Button>
                            </div>
                    )
                : userData?.state === 'half_done' ?
                    <HalfDone variant={'secondary'} />
                : [
                    ...paidLikes,
                    ...freeLikes
                ].length ?
                    <>
                        { paidProfileList }
                        {
                            !isPaid &&
                            [
                                ...paidLikes,
                                ...freeLikes
                            ].length > 0 &&
                            userData
                                ?.moderation_status
                                !== 'change_required' &&
                            <div className={styles.dekorHeart} />
                        }            
                        {
                            !isPaid &&
                            [
                                ...paidLikes,
                                ...freeLikes
                            ].length > 0 && (
                            <Typography
                                className={styles.title}
                                center
                                fontWeight="semibold"
                                fontFamily="SFProText">
                                {t('likes.someone_already_liked_you')}
                            </Typography>
                        )}
                        {
                            !isPaid &&
                            [
                                ...paidLikes,
                                ...freeLikes
                            ].length > 0 && (
                            <>
                                <Typography
                                    center
                                    className={styles.text}
                                    fontWeight="normal"
                                    fontFamily="SFProText">
                                    <Trans i18nKey="likes.likes_note" />
                                </Typography>
                                <div className={styles.buttonWrapper}>
                                    <Button
                                        typeColor="orange"
                                        className={styles.button}
                                        isLink
                                        to={routesApp.subscription}
                                        onClick={onActivatePremiumClick}>
                                        <span>{t('activate_premium')}</span>
                                        <MagicStick width={20} height={20} />
                                    </Button>
                                    <Button
                                        typeColor="white"
                                        className={styles.buttonWhite}
                                        isLink
                                        to={routesApp.browse}>
                                        <span>{t('likes.to_browse')}</span>
                                    </Button>
                                </div>
                            </>
                        )}
                        { freeProfileList }
                    </>
                : noLikeYet
            }
        </Container>
    );
};
